import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  Plus,
  Download,
  Building2,
  Calendar,
  FileText,
  Percent,
  Calculator,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfitLoss() {
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [plData, setPlData] = useState([]);
  const [selectedStore, setSelectedStore] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('weekly');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    store_id: '',
    period_ending: '',
    gross_sales: '',
    discounts: '',
    delivery_sales: '',
    delivery_fees: '',
    food_cost: '',
    paper_cost: '',
    oil_cost: '',
    labor_cost: '',
    rent: '',
    utilities: '',
    marketing: '',
    other_expenses: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [selectedStore]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [storesRes, plRes] = await Promise.all([
        axios.get(`${API_URL}/stores`),
        axios.get(`${API_URL}/reports/profit-loss`, {
          params: selectedStore !== 'all' ? { store_id: selectedStore } : {}
        })
      ]);
      setStores(storesRes.data);
      setPlData(plRes.data.entries || []);
      // summary is returned from backend but we still compute client-side for display flexibility
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    if (plData.length === 0) return null;
    
    const totals = {
      gross_sales: plData.reduce((s, d) => s + (d.gross_sales || 0), 0),
      net_sales: plData.reduce((s, d) => s + (d.net_sales || 0), 0),
      avg_food_cost: plData.reduce((s, d) => s + (d.food_cost_pct || 0), 0) / plData.length,
      avg_labor: plData.reduce((s, d) => s + (d.labor_cost_pct || 0), 0) / plData.length,
      avg_prime_cost: plData.reduce((s, d) => s + (d.prime_cost_pct || 0), 0) / plData.length,
      total_controllable_profit: plData.reduce((s, d) => s + (d.controllable_profit || 0), 0),
      avg_growth: plData.reduce((s, d) => s + (d.sales_growth || 0), 0) / plData.length
    };
    
    totals.food_cost_dollar = totals.gross_sales * (totals.avg_food_cost / 100);
    totals.labor_cost_dollar = totals.gross_sales * (totals.avg_labor / 100);
    totals.gross_profit = totals.gross_sales - totals.food_cost_dollar;
    totals.operating_profit = totals.gross_profit - totals.labor_cost_dollar;
    totals.profit_margin = (totals.operating_profit / totals.gross_sales * 100) || 0;
    
    return totals;
  };

  const totals = calculateTotals();

  const handleDownloadPL = () => {
    // Generate CSV P&L report
    const headers = ['Store', 'Period', 'Gross Sales', 'Net Sales', 'Food Cost %', 'Labor %', 'Prime Cost %', 'Controllable Profit', 'Growth %'];
    const rows = plData.map(d => [
      d.store_name,
      d.period,
      `$${d.gross_sales?.toLocaleString()}`,
      `$${d.net_sales?.toLocaleString()}`,
      `${d.food_cost_pct}%`,
      `${d.labor_cost_pct}%`,
      `${d.prime_cost_pct}%`,
      `$${d.controllable_profit?.toLocaleString()}`,
      `${d.sales_growth}%`
    ]);
    
    // Add totals row
    if (totals) {
      rows.push([]);
      rows.push([
        'TOTAL/AVERAGE',
        '',
        `$${totals.gross_sales?.toLocaleString()}`,
        `$${totals.net_sales?.toLocaleString()}`,
        `${totals.avg_food_cost?.toFixed(1)}%`,
        `${totals.avg_labor?.toFixed(1)}%`,
        `${totals.avg_prime_cost?.toFixed(1)}%`,
        `$${totals.total_controllable_profit?.toLocaleString()}`,
        `${totals.avg_growth?.toFixed(1)}%`
      ]);
    }
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `profit_loss_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('P&L Report downloaded');
  };

  if (!['owner', 'area_manager', 'manager'].includes(user?.role)) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
        <h2 className="text-lg font-bold text-slate-900 mb-2">Access Restricted</h2>
        <p className="text-slate-600">Only owners can view P&L reports</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pl-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Calculator className="w-8 h-8" />
              Profit & Loss Report
            </h1>
            <p className="text-white/80 text-sm mt-1">Financial performance across all stores</p>
          </div>
          <Button onClick={handleDownloadPL} className="bg-white text-orange-600 hover:bg-orange-50 rounded-sm font-semibold">
            <Download className="w-4 h-4 mr-2" />
            Download P&L
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        {stores.length > 0 && (
          <Select value={selectedStore} onValueChange={setSelectedStore}>
            <SelectTrigger className="w-48 rounded-sm border-slate-200">
              <Building2 className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="All Stores" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stores</SelectItem>
              {stores.map((store) => (
                <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* P&L Summary Cards */}
      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="bg-white border border-slate-200 rounded-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-5 h-5 text-green-500" />
              </div>
              <p className="text-xl font-bold text-slate-900">${totals.gross_sales?.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
              <p className="text-xs text-slate-500">Gross Sales</p>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 rounded-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <Percent className="w-5 h-5 text-orange-500" />
                <span className="text-xs px-2 py-0.5 rounded-full bg-orange-50 text-orange-600">COGS</span>
              </div>
              <p className="text-xl font-bold text-slate-900">{totals.avg_food_cost?.toFixed(1)}%</p>
              <p className="text-xs text-slate-500">Food Cost</p>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 rounded-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-5 h-5 text-blue-500" />
              </div>
              <p className="text-xl font-bold text-slate-900">${totals.gross_profit?.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
              <p className="text-xs text-slate-500">Gross Profit</p>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 rounded-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <Percent className="w-5 h-5 text-purple-500" />
                <span className="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-600">Labor</span>
              </div>
              <p className="text-xl font-bold text-slate-900">{totals.avg_labor?.toFixed(1)}%</p>
              <p className="text-xs text-slate-500">Labor Cost</p>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 rounded-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-5 h-5 text-emerald-500" />
              </div>
              <p className="text-xl font-bold text-slate-900">${totals.operating_profit?.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
              <p className="text-xs text-slate-500">Operating Profit</p>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 rounded-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-5 h-5 text-green-500" />
                <span className={`text-xs px-2 py-0.5 rounded-full ${totals.profit_margin >= 10 ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>Margin</span>
              </div>
              <p className="text-xl font-bold text-slate-900">{totals.profit_margin?.toFixed(1)}%</p>
              <p className="text-xs text-slate-500">Profit Margin</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* P&L Statement Table */}
      <Card className="bg-white border border-slate-200 rounded-sm">
        <CardHeader className="pb-2 border-b border-slate-100">
          <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <FileText className="w-5 h-5 text-green-600" />
            Income Statement by Store
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Store</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Period</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Gross Sales</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Food %</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Labor %</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Prime Cost %</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Profit</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Growth</th>
                </tr>
              </thead>
              <tbody>
                {plData.map((row, index) => (
                  <tr key={index} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-3 px-4 font-medium text-slate-900">{row.store_name}</td>
                    <td className="py-3 px-4 text-right text-slate-600">{row.period}</td>
                    <td className="py-3 px-4 text-right font-medium">${row.gross_sales?.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right">
                      <span className={`px-2 py-0.5 rounded text-xs ${row.food_cost_pct <= 32 ? 'bg-green-50 text-green-600' : row.food_cost_pct <= 35 ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`}>
                        {row.food_cost_pct}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`px-2 py-0.5 rounded text-xs ${row.labor_cost_pct <= 19 ? 'bg-green-50 text-green-600' : row.labor_cost_pct <= 22 ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`}>
                        {row.labor_cost_pct}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`px-2 py-0.5 rounded text-xs ${row.prime_cost_pct <= 51 ? 'bg-green-50 text-green-600' : row.prime_cost_pct <= 55 ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`}>
                        {row.prime_cost_pct}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right font-medium text-green-600">
                      ${row.controllable_profit?.toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`flex items-center justify-end gap-1 ${row.sales_growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {row.sales_growth >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                        {row.sales_growth}%
                      </span>
                    </td>
                  </tr>
                ))}
                {totals && (
                  <tr className="bg-slate-100 font-semibold">
                    <td className="py-3 px-4 text-slate-900">TOTAL / AVG</td>
                    <td className="py-3 px-4 text-right text-slate-600">{plData.length} entries</td>
                    <td className="py-3 px-4 text-right">${totals.gross_sales?.toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                    <td className="py-3 px-4 text-right">{totals.avg_food_cost?.toFixed(1)}%</td>
                    <td className="py-3 px-4 text-right">{totals.avg_labor?.toFixed(1)}%</td>
                    <td className="py-3 px-4 text-right">{totals.avg_prime_cost?.toFixed(1)}%</td>
                    <td className="py-3 px-4 text-right text-green-600">${totals.total_controllable_profit?.toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                    <td className="py-3 px-4 text-right">{totals.avg_growth?.toFixed(1)}%</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* P&L Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border border-slate-200 rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Revenue Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-slate-600">Gross Sales</span>
                <span className="font-semibold">${totals?.gross_sales?.toLocaleString(undefined, {maximumFractionDigits: 0}) || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100 text-red-600">
                <span>Less: Discounts & Promos</span>
                <span>-${((totals?.gross_sales || 0) - (totals?.net_sales || 0)).toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
              </div>
              <div className="flex justify-between items-center py-2 font-semibold text-lg">
                <span className="text-slate-900">Net Sales</span>
                <span className="text-green-600">${totals?.net_sales?.toLocaleString(undefined, {maximumFractionDigits: 0}) || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Cost Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-slate-600">Food Cost ({totals?.avg_food_cost?.toFixed(1)}%)</span>
                <span className="text-red-600">-${totals?.food_cost_dollar?.toLocaleString(undefined, {maximumFractionDigits: 0}) || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-slate-600">Labor Cost ({totals?.avg_labor?.toFixed(1)}%)</span>
                <span className="text-red-600">-${totals?.labor_cost_dollar?.toLocaleString(undefined, {maximumFractionDigits: 0}) || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-slate-600">Prime Cost (Food + Labor)</span>
                <span className="text-amber-600">{totals?.avg_prime_cost?.toFixed(1)}%</span>
              </div>
              <div className="flex justify-between items-center py-2 font-semibold text-lg">
                <span className="text-slate-900">Operating Profit</span>
                <span className="text-green-600">${totals?.operating_profit?.toLocaleString(undefined, {maximumFractionDigits: 0}) || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
